import React, { useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { CircleCheck, CircleDashed, CircleX, Download, Receipt } from 'lucide-react';
import {
  api, displayName, downloadCsv, fmtDate, fmtDay, fmtInt, fmtMoney, humanize, parseDate, useQuery, useUsers,
} from './lib';
import {
  Btn, EmptyState, ErrorState, Page, Pagination, Panel, Pill, RefreshButton, RowsSkeleton,
  SearchInput, Segmented, StatCard, StatGrid, StatSkeletons, Table, TD, TH, TR, usePagination,
} from './ui';
import { ChartTooltip, COLORS, cursorFill, gridProps, legendProps, xAxisProps, yAxisProps } from './charts';

const PERIODS = [
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '12 months' },
];

const PROVIDER_LABEL = { stripe: 'Stripe (web)', iap_ios: 'App Store', iap_android: 'Google Play' };
const STATUS_TONE = {
  paid: 'success', succeeded: 'success', active: 'success',
  pending: 'warning', processing: 'warning',
  refunded: 'danger', failed: 'danger', canceled: 'danger', cancelled: 'danger', expired: 'danger', revoked: 'danger', unpaid: 'danger',
};

function Source({ name, state, children }) {
  const icon = state === 'ok'
    ? <CircleCheck className="h-4 w-4 text-emerald-600" />
    : state === 'error' ? <CircleX className="h-4 w-4 text-red-600" /> : <CircleDashed className="h-4 w-4 text-muted-foreground" />;
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <span className="mt-0.5">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{name}</p>
        <div className="mt-0.5 space-y-0.5 text-xs text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

/* Fill every day of the window so quiet days show as zero, not as gaps. */
function dailySeries(daily, days) {
  const byDay = Object.fromEntries((daily || []).map((d) => [d.date, d]));
  const out = [];
  const end = new Date();
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(end);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    out.push({ date: key, stripe: byDay[key]?.stripe || 0, iap: byDay[key]?.iap || 0 });
  }
  return out;
}

/* Group days into weeks for the 12-month view so bars stay readable. */
function weekly(series) {
  const out = [];
  for (let i = 0; i < series.length; i += 7) {
    const chunk = series.slice(i, i + 7);
    out.push({
      date: chunk[0].date,
      stripe: chunk.reduce((s, d) => s + d.stripe, 0),
      iap: chunk.reduce((s, d) => s + d.iap, 0),
    });
  }
  return out;
}

export default function RevenuePanel({ onOpenCustomer }) {
  const [days, setDays] = useState(90);
  const { data, error, loading, refetch, updatedAt } = useQuery(
    `revenue:${days}:live`,
    () => api.get(`/admin/revenue?days=${days}`),
    { maxAge: 5 * 60_000 },
  );
  const { data: users } = useUsers();
  const [tab, setTab] = useState('transactions');
  const [query, setQuery] = useState('');
  const [provider, setProvider] = useState('all');

  const userById = useMemo(() => Object.fromEntries((users || []).map((u) => [u.user_id, u])), [users]);
  const summary = data?.summary || {};
  const currencies = Object.entries(summary.by_currency || {}).sort((a, b) => b[1] - a[1]);
  const [mainCurrency, mainAmount] = currencies[0] || ['EUR', 0];

  const chartData = useMemo(() => {
    const series = dailySeries(data?.daily_revenue, days);
    return days > 120 ? weekly(series) : series;
  }, [data, days]);

  const transactions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.recent_transactions || [])
      .filter((t) => provider === 'all' || t.provider === provider)
      .filter((t) => {
        if (!q) return true;
        const u = userById[t.user_id];
        return [t.id, t.user_id, t.plan, u?.email, u?.name].some((v) => (v || '').toLowerCase().includes(q));
      });
  }, [data, query, provider, userById]);
  const pager = usePagination(transactions, 25, `${query}|${provider}|${days}`);
  const charges = data?.stripe?.recent_charges || [];

  const stripe = data?.stripe || {};
  const play = data?.google_play || {};
  const apple = data?.apple || {};

  const exportCsv = () => downloadCsv(
    `transactions_${new Date().toISOString().slice(0, 10)}.csv`,
    ['Date', 'Transaction', 'User ID', 'Email', 'Provider', 'Plan', 'Amount', 'Currency', 'Status'],
    transactions.map((t) => [t.date, t.id, t.user_id, userById[t.user_id]?.email, t.provider, t.plan, t.amount, t.currency, t.status]),
  );

  return (
    <Page
      title="Revenue"
      description="Payments recorded in the database, plus live figures from Stripe, Google Play and App Store Connect. The live part can take a few seconds."
      actions={(
        <>
          <Segmented value={days} onChange={setDays} options={PERIODS} />
          <RefreshButton onClick={refetch} loading={loading} updatedAt={updatedAt} />
        </>
      )}
    >
      {error && <ErrorState error={error} onRetry={refetch} />}

      {!data && loading ? (
        <StatSkeletons count={4} />
      ) : data && (
        <StatGrid>
          <StatCard
            label="Revenue"
            value={fmtMoney(mainAmount, mainCurrency)}
            hint={currencies.length > 1
              ? `+ ${currencies.slice(1).map(([c, v]) => fmtMoney(v, c)).join(' + ')}`
              : `Last ${PERIODS.find((p) => p.value === days)?.label}`}
          />
          <StatCard label="Payments" value={fmtInt(summary.transaction_count)} hint={`${fmtInt(summary.lifetime_count)} lifetime purchases`} />
          <StatCard
            label="Paid in the apps"
            value={fmtInt((summary.count_by_provider?.iap_ios || 0) + (summary.count_by_provider?.iap_android || 0))}
            hint={`${fmtInt(summary.count_by_provider?.iap_ios)} App Store · ${fmtInt(summary.count_by_provider?.iap_android)} Google Play · ${fmtInt(summary.count_by_provider?.stripe)} web`}
          />
          <StatCard
            label="Stripe balance"
            value={stripe.balance ? fmtMoney(stripe.balance.available, stripe.balance.currency) : '—'}
            hint={stripe.balance ? `${fmtMoney(stripe.balance.pending, stripe.balance.currency)} pending` : 'Needs a Stripe secret key'}
          />
        </StatGrid>
      )}

      {data && (
        <div className="grid gap-4 lg:grid-cols-3">
          <Panel
            title="Revenue over time"
            description={`${days > 120 ? 'Per week' : 'Per day'}, by where the payment happened. Amounts in different currencies are added together here.`}
            className="lg:col-span-2"
          >
            {chartData.some((d) => d.stripe || d.iap) ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                    <CartesianGrid {...gridProps} />
                    <XAxis dataKey="date" tickFormatter={fmtDay} {...xAxisProps} />
                    <YAxis {...yAxisProps} allowDecimals />
                    <Tooltip cursor={cursorFill} content={<ChartTooltip valueFormatter={(v) => (v || 0).toFixed(2)} />} />
                    <Legend {...legendProps} />
                    <Bar dataKey="stripe" name="Web (Stripe)" stackId="r" fill={COLORS.ink} maxBarSize={22} />
                    <Bar dataKey="iap" name="In-app" stackId="r" fill={COLORS.ink2} radius={[3, 3, 0, 0]} maxBarSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState icon={Receipt} title="No payments in this period" />
            )}
          </Panel>

          <Panel title="Sources" description="What each provider reports right now" flush>
            <div className="divide-y divide-border">
              <Source name="Stripe" state={stripe.balance ? 'ok' : 'off'}>
                {stripe.balance ? (
                  <>
                    <p>{fmtInt(stripe.active_subscriptions)} legacy subscriptions still active</p>
                    {stripe.mrr > 0 && <p>{fmtMoney(stripe.mrr, 'EUR')} a month from those subscriptions</p>}
                  </>
                ) : <p>Not connected: live data needs the Stripe secret key.</p>}
              </Source>
              <Source name="Google Play" state={play.error ? 'error' : 'ok'}>
                {play.error ? <p className="break-words">{play.error}</p> : (
                  <>
                    <p>{fmtMoney(play.total, play.currency || 'EUR')} from {fmtInt(play.transaction_count)} payments</p>
                    <p>{fmtInt(play.active_subs)} legacy subscriptions active</p>
                  </>
                )}
              </Source>
              <Source name="App Store" state={apple.error ? 'error' : 'ok'}>
                {apple.error ? <p className="break-words">{apple.error}</p> : (
                  <p>{fmtMoney(apple.total, apple.currency || 'USD')} in proceeds (sales reports, by month)</p>
                )}
              </Source>
            </div>
          </Panel>
        </div>
      )}

      {data && (
        <Panel
          flush
          title={tab === 'transactions' ? 'Latest payments' : 'Stripe charges'}
          description={tab === 'transactions'
            ? 'The 100 most recent payment records, from every provider'
            : 'The latest charges in Stripe, including failed ones'}
          actions={(
            <>
              <Segmented
                value={tab}
                onChange={setTab}
                options={[
                  { value: 'transactions', label: 'Payments', count: data.recent_transactions?.length || 0 },
                  { value: 'charges', label: 'Stripe charges', count: charges.length },
                ]}
              />
              {tab === 'transactions' && (
                <Btn variant="outline" size="sm" onClick={exportCsv} disabled={!transactions.length}>
                  <Download className="h-3.5 w-3.5" /><span className="hidden sm:inline">CSV</span>
                </Btn>
              )}
            </>
          )}
        >
          {tab === 'transactions' ? (
            <>
              <div className="flex flex-col gap-3 border-b border-border p-3 sm:flex-row sm:items-center sm:justify-between">
                <SearchInput value={query} onChange={setQuery} placeholder="Customer, email, plan, ID" />
                <Segmented
                  value={provider}
                  onChange={setProvider}
                  options={[{ value: 'all', label: 'All' }, ...Object.entries(PROVIDER_LABEL).map(([value, label]) => ({ value, label }))]}
                />
              </div>
              {transactions.length === 0 ? (
                <EmptyState icon={Receipt} title="No payments match" />
              ) : (
                <>
                  <Table>
                    <thead>
                      <tr>
                        <TH>Date</TH>
                        <TH>Customer</TH>
                        <TH className="hidden sm:table-cell">Provider</TH>
                        <TH className="hidden md:table-cell">Plan</TH>
                        <TH>Status</TH>
                        <TH align="right">Amount</TH>
                      </tr>
                    </thead>
                    <tbody>
                      {pager.slice.map((t, i) => {
                        const u = userById[t.user_id];
                        return (
                          <TR key={`${t.id}-${i}`} onClick={u ? () => onOpenCustomer(u.user_id) : undefined}>
                            <TD className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(t.date)}</TD>
                            <TD>
                              {u ? (
                                <div className="min-w-0">
                                  <p className="truncate text-sm text-foreground">{displayName(u)}</p>
                                  <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                                </div>
                              ) : (
                                <span className="font-mono text-xs text-muted-foreground">{t.user_id ? `${t.user_id.slice(0, 8)}…` : '—'}</span>
                              )}
                            </TD>
                            <TD className="hidden sm:table-cell"><Pill>{PROVIDER_LABEL[t.provider] || t.provider}</Pill></TD>
                            <TD className="hidden text-xs text-muted-foreground md:table-cell">{humanize(t.plan) || '—'}</TD>
                            <TD><Pill tone={STATUS_TONE[(t.status || '').toLowerCase()] || 'neutral'}>{humanize(t.status) || 'Unknown'}</Pill></TD>
                            <TD align="right" className="whitespace-nowrap font-medium">{fmtMoney(t.amount, t.currency || 'EUR')}</TD>
                          </TR>
                        );
                      })}
                    </tbody>
                  </Table>
                  <Pagination {...pager} noun="payments" />
                </>
              )}
            </>
          ) : charges.length === 0 ? (
            <EmptyState icon={Receipt} title="No Stripe charges" description={stripe.balance ? 'None in this period.' : 'Live Stripe data needs the Stripe secret key.'} />
          ) : (
            <Table>
              <thead>
                <tr>
                  <TH>Date</TH>
                  <TH>Description</TH>
                  <TH>Status</TH>
                  <TH align="right">Amount</TH>
                </tr>
              </thead>
              <tbody>
                {charges.map((ch) => (
                  <TR key={ch.id}>
                    <TD className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(parseDate(ch.created))}</TD>
                    <TD>
                      <p className="truncate text-sm text-foreground">{ch.description || 'No description'}</p>
                      <p className="truncate font-mono text-[11px] text-muted-foreground">{ch.id}</p>
                    </TD>
                    <TD><Pill tone={STATUS_TONE[(ch.status || '').toLowerCase()] || 'neutral'}>{humanize(ch.status)}</Pill></TD>
                    <TD align="right" className="whitespace-nowrap font-medium">{fmtMoney(ch.amount, ch.currency || 'EUR')}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </Panel>
      )}

      {!data && loading && <Panel flush><RowsSkeleton rows={6} /></Panel>}
    </Page>
  );
}
